import { Input } from '@chakra-ui/react';
import { useAppDispatch } from 'modules/Editor/store';
import { updateForm } from 'modules/Editor/store/interactionSlice';
import { MouseEvent, forwardRef, useRef, useState } from 'react';
import { WidgetState } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.types';
import { useWidget } from 'widgets/sdk';

import { FORM_INPUT_TEST_ID } from '../FormWidget.config';
import { FormType, FormWidgetData, ShortQuestionProps } from '../FormWidget.types';
import { InputBase } from '../InputBase';

const ShortQuestion = forwardRef<HTMLLabelElement, ShortQuestionProps>(
  (
    {
      disableTabbability,
      storedValue = '',
      isReadOnly = false,
      isLabelFocused = false,
      setWidgetState = (_: WidgetState) => {},
    },
    ref,
  ) => {
    const { widgetId, style, field: formField, labelPosition, isWidgetSelected } = useWidget<FormWidgetData>();
    const { gap, borderRadius, backgroundColor, borderColor, borderWidth, fontSize, ...labelStyles } = style;
    const inputRef = useRef<HTMLInputElement>(null);
    const [value, setValue] = useState(storedValue);
    const dispatch = useAppDispatch();

    const handleInputClick = () => {
      inputRef.current?.focus();
      setWidgetState(WidgetState.Active);
    };

    const handleDoubleClick = (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
    };

    const handleLabelTextClick = (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setWidgetState(WidgetState.Edit);
    };

    const handleLabelClick = (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setWidgetState(WidgetState.Active);
    };

    const handleInputSubmit = () => {
      if (!isReadOnly) return;

      dispatch(
        updateForm({
          id: widgetId,
          fields: {
            label: formField.label,
            input: value,
            value: value,
          },
        }),
      );
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
    };

    return (
      <InputBase
        id={`form-shortQuestion-${widgetId}`}
        ref={ref}
        formType={FormType.ShortQuestion}
        label={formField.label}
        fontSize={fontSize}
        labelStyles={{ gap, ...labelStyles }}
        labelPosition={labelPosition}
        isLabelFocused={isWidgetSelected && isLabelFocused}
        isReadOnly={isReadOnly}
        onLabelClick={handleLabelClick}
        onLabelTextClick={handleLabelTextClick}
        containerProps={{
          pointerEvents: disableTabbability ? 'none' : 'auto',
        }}
      >
        <Input
          ref={inputRef}
          type={formField.type}
          data-testid={FORM_INPUT_TEST_ID}
          value={value}
          style={{
            backgroundColor,
            borderRadius,
            borderColor,
            borderWidth,
          }}
          placeholder={formField.placeholder}
          onClick={handleInputClick}
          onDoubleClick={handleDoubleClick}
          onBlur={handleInputSubmit}
          onChange={handleInputChange}
          tabIndex={disableTabbability ? -1 : 0}
          _hover={{
            borderColor: 'upgrade.blue.500',
          }}
          _focusVisible={{
            borderColor,
          }}
          _placeholder={{
            fontSize: `${fontSize}px`,
            fontFamily: labelStyles.fontFamily,
          }}
        />
      </InputBase>
    );
  },
);
ShortQuestion.displayName = 'ShortQuestion';

export default ShortQuestion;
